import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Badge } from '@resource-kit/ui/react';
import { useToday } from '@resource-kit/controllers/react';
import { queryKeys } from '@lib/queryKeys';
import { REVIEW_QUEUE_KIND, pendingViewParams, reviewQueueAPI } from '@/services/review-queue';
import { OverviewCard } from './OverviewCard';

const INBOX_ROWS = [
  {
    kind: REVIEW_QUEUE_KIND.POST_CUTOFF_ORDER_CHANGE,
    label: 'reviewQueue.kind.post_cutoff_order_change',
    sub: 'overview.inbox.sub.post_cutoff_order_change',
  },
  {
    kind: REVIEW_QUEUE_KIND.POST_DISPATCH_CANCELLATION,
    label: 'reviewQueue.kind.post_dispatch_cancellation',
    sub: 'overview.inbox.sub.post_dispatch_cancellation',
  },
  {
    kind: REVIEW_QUEUE_KIND.DISPATCH_FAILED,
    label: 'reviewQueue.kind.dispatch_failed',
    sub: 'overview.inbox.sub.dispatch_failed',
  },
] as const;

/** Same freshness as `usePendingReviewCount`: the inbox is operator-actionable. */
const COUNT_STALE_MS = 5_000;
/** 30s focused-tab refetch; quiet in the background. */
const BACKGROUND_REFETCH_MS = 30_000;

/**
 * PENDING operator-inbox items per kind — open AND raised today, the same
 * meaning as `usePendingReviewCount` and the inbox's Pending tab the admin link
 * opens (an unbounded `status=open` count only grows; #1998/#2004). The day
 * bound rides in the query key so the count re-reads at merchant midnight.
 */
export function InboxCard({ isAdmin, className }: { isAdmin: boolean; className?: string }) {
  const { t } = useTranslation();
  const pending = pendingViewParams(useToday());
  const counts = useQueries({
    queries: INBOX_ROWS.map(({ kind }) => ({
      queryKey: [...queryKeys.reviewQueue.countByKind(kind), pending.filters['created_at']],
      queryFn: () =>
        reviewQueueAPI
          .list({ ...pending, page: 1, size: 1, filters: { ...pending.filters, kind } })
          .then((r) => r.total),
      staleTime: COUNT_STALE_MS,
      refetchInterval: BACKGROUND_REFETCH_MS,
      refetchIntervalInBackground: false,
    })),
  });
  const allLoaded = counts.every((c) => c.data !== undefined);
  const sum = counts.reduce((acc, c) => acc + (c.data ?? 0), 0);
  return (
    <OverviewCard
      title={t('nav.reviewQueue')}
      aside={
        <Badge data-testid="overview-inbox-total" variant={sum > 0 ? 'destructive' : 'secondary'}>
          {allLoaded ? sum : '–'}
        </Badge>
      }
      link={isAdmin ? { to: '/admin/review-queue', label: t('overview.inbox.open') } : undefined}
      className={className}
    >
      {INBOX_ROWS.map(({ kind, label, sub }, i) => {
        const count = counts[i];
        return (
          <div
            key={kind}
            data-testid={`overview-inbox-${kind}`}
            className="flex items-center justify-between border-b py-2 last:border-b-0"
          >
            <span>
              {t(label)}
              <span className="block text-xs text-muted-foreground">{t(sub)}</span>
            </span>
            <span className="font-mono">
              {count?.data ?? (count?.isError ? t('overview.loadError') : '–')}
            </span>
          </div>
        );
      })}
    </OverviewCard>
  );
}
